import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, ArrowRight, Sparkles, Shield, Zap, BarChart3, FileText, Wallet, Loader2, X, Copy, BookOpen, Lock, Globe, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ToothmatrixTour from "@/components/ToothmatrixTour";
import PrivacyConsentCheckbox, { usePdpplPolicy } from "@/components/PrivacyConsentCheckbox";
import SecuritySection from "@/pages/toothmatrix/SecuritySection";
import GlobalReachSection from "@/pages/toothmatrix/GlobalReachSection";
import { LabPricingSlider } from "@/components/LabPricingSlider";
import { BrandFilm } from "@/components/BrandFilm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Toothmatrix Lab — Dental Lab Management. The full marketing page for the
 * lab-side SaaS product (moved from /toothmatrix, which is now the two-product
 * chooser page). Pricing is usage-based (USD) via the shared LabPricingSlider.
 */
export default function DentalLabs() {
    const nav = useNavigate();
    // Preview state — propagates company name / slug / logo through the demo-request CTA.
    const [preview, setPreview] = useState({ company_name: "", slug: "", color: "#0E7490" });

    // Iteration 39C — replaced the auto-provisioning 7-day trial with a
    // human-loop "Request a Demo" flow. Both buttons (hero + pricing card)
    // now route the prospect to /contact with a `demo-request` subject
    // pre-selected so the support inbox receives a structured lead instead
    // of auto-creating a tenant.
    const startTrial = (planKey) => {
        const next = `/contact?subject=demo-request-lab&message=${encodeURIComponent(`Hi — I'd like to request a demo of Toothmatrix Lab (interested in the ${planKey} plan). My lab is …`)}`;
        nav(next);
    };

    // From the live preview block — pre-fills company + slug into the demo request.
    const claimSubdomain = () => {
        nav(`/contact?subject=demo-request-lab&message=${encodeURIComponent("Hi — I'd like a demo of Toothmatrix Lab and to claim the subdomain I previewed.")}`);
    };

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900" data-testid="solutions-dental-labs-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1120] via-[#0e2e3a] to-[#0B1120] text-white">
                <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_30%_20%,rgba(14,165,233,0.18),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(56,189,248,0.12),transparent_55%)]" />
                <div className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-20 pb-28">
                    <div className="font-mono-tr text-[11px] tracking-[0.28em] uppercase text-cyan-300 mb-4" data-testid="dl-product-eyebrow">
                        Toothmatrix Lab · Dental Lab Management
                    </div>
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-cyan-200 text-[11px] font-mono-tr tracking-[0.16em] uppercase mb-5" data-testid="hero-region-pill">
                        <Globe size={11} /> From Qatar · serving labs across 6+ countries
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight max-w-4xl leading-[1.04]">
                        The operating system for modern dental labs.
                    </h1>
                    <p className="mt-7 text-lg text-slate-300/95 max-w-2xl leading-relaxed">
                        Cases · invoices · prepaid credits · payroll · sales pipeline · 10-year warranty PDFs. One platform.
                        White-labeled to your lab. Request a demo &mdash; we&rsquo;ll show you the platform live and onboard you when you&rsquo;re ready.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <button
                            onClick={() => startTrial("annual")}
                            data-testid="hero-request-demo"
                            className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] font-medium text-[14px] hover:bg-cyan-300 transition"
                        >
                            <Sparkles size={15} /> Request a demo
                        </button>
                        <a href="#pricing" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl border border-white/15 text-[14px] text-white/90 hover:bg-white/5 transition">
                            See pricing <ArrowRight size={14} />
                        </a>
                    </div>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
                        {[
                            { icon: Zap, label: "Set up in minutes" },
                            { icon: Shield, label: "Bank-grade security" },
                            { icon: BarChart3, label: "Live dashboard" },
                            { icon: FileText, label: "Auto invoices + PDFs" },
                        ].map((f) => (
                            <div key={f.label} className="flex items-center gap-2 text-[12.5px] text-cyan-100/80">
                                <f.icon size={14} className="text-cyan-300 shrink-0" /> {f.label}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cross-sell: the sibling product for clinics */}
            <section className="bg-cyan-50 border-b border-cyan-100">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[13.5px] text-slate-700">
                        <span className="font-semibold text-slate-900">Running a dental practice instead?</span>{" "}
                        Toothmatrix Practice is our free Scan Inbox for clinics — send scans from any intraoral scanner.
                    </p>
                    <Link to="/solutions/dental-practice" data-testid="dl-crosslink-practice"
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-cyan-700 hover:text-cyan-500">
                        Explore Toothmatrix Practice <ArrowRight size={13} />
                    </Link>
                </div>
            </section>

            {/* BRAND FILM — every visitor path sees the film */}
            <section className="max-w-6xl mx-auto px-6 sm:px-8 pt-16 -mb-2" data-testid="dl-brand-film-section">
                <div className="relative aspect-video rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-30px_rgba(8,47,73,0.35)]">
                    <BrandFilm />
                </div>
            </section>

            {/* LIVE BRAND PREVIEW — drives conversion by letting prospects "see" their own brand before paying */}
            <BrandPreview preview={preview} setPreview={setPreview} onClaim={claimSubdomain} />

            {/* FEATURES */}
            <section className="max-w-6xl mx-auto px-6 sm:px-8 py-20">
                <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3">· Everything your lab needs</div>
                <h2 className="text-3xl font-display tracking-tight mb-2">Built by a working lab. For working labs.</h2>
                <p className="text-slate-600 max-w-2xl">Eight modules, one login. Every workflow your team uses today, faster and more reliable.</p>
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                        { icon: BarChart3, title: "Live Dashboard", body: "KPIs, late-case chips, technician workload, Kanban board with WIP limits, auto-balance day." },
                        { icon: FileText, title: "Cases & Workflow", body: "Submission to delivery, with chat, drawings, 3D STL viewer, status timeline, and activity feed." },
                        { icon: Wallet, title: "Invoices & Credits", body: "Stripe payments, prepaid credit float, auto-top-up via SetupIntent, automatic settlement on accepted cases." },
                        { icon: Shield, title: "10-year Warranty PDF", body: "Branded warranty cards generated on every delivered case, QR-verifiable, signed with your logo." },
                        { icon: Sparkles, title: "Finance & Payroll", body: "Expenses, employees, monthly runs, payslip PDFs, bank-export CSV, year-end Form 16." },
                        { icon: Zap, title: "Sales Pipeline", body: "Lead → demo → trial → active, drag-and-drop Kanban, activity logging, target tracking with auto-incentives." },
                    ].map((f) => (
                        <div key={f.title} className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-cyan-300 transition">
                            <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 grid place-items-center mb-3">
                                <f.icon size={16} />
                            </div>
                            <h3 className="font-display text-[17px] tracking-tight mb-1.5">{f.title}</h3>
                            <p className="text-[13.5px] text-slate-600 leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SECURITY & COMPLIANCE — patient data is sacred. This section closes deals in healthcare. */}
            <SecuritySection />

            {/* GLOBAL REACH — "From Qatar, for the World" — map + compliance matrix */}
            <GlobalReachSection />

            {/* PRODUCT TOUR — scrolling screen-by-screen showcase */}
            <ToothmatrixTour />

            {/* PRICING — usage-based (USD): pick your monthly case volume */}
            <section id="pricing" className="bg-white py-20 border-y border-slate-200">
                <div className="max-w-6xl mx-auto px-6 sm:px-8">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3">· Pricing</div>
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-display tracking-tight">Pay for what you produce.</h2>
                            <p className="mt-4 text-slate-600 max-w-md leading-relaxed">
                                Usage-based pricing in USD — pick your monthly case volume and that&rsquo;s your price.
                                Every feature included on every tier: unlimited users, white-label branding, free
                                practice portals for your clinics.
                            </p>
                            <ul className="mt-6 space-y-2.5 text-[13.5px] text-slate-700">
                                {["No hidden fees — the slider price is the whole price", "No upfront payments or setup fees", "3-month money-back guarantee", "Yearly billing saves 15%"].map((f) => (
                                    <li key={f} className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> {f}</li>
                                ))}
                            </ul>
                            <Link to="/pricing/labs" data-testid="dl-full-pricing-link"
                                className="inline-flex items-center gap-1.5 mt-6 text-[13.5px] font-medium text-cyan-700 hover:text-cyan-500">
                                See full pricing details <ArrowRight size={13} />
                            </Link>
                        </div>
                        <LabPricingSlider />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-6 sm:px-8 py-20">
                <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3">· FAQ</div>
                <h2 className="text-3xl font-display tracking-tight mb-8">Common questions.</h2>
                <div className="space-y-5">
                    {[
                        { id: "demo-how", q: "How does the demo work?", a: "Submit your details on the contact form (or click 'Request a demo'). One of our team replies within one business day to schedule a 30-minute walkthrough on your real workflows. After the demo, you pick a plan and we onboard your lab on a unique subdomain." },
                        { id: "card-required", q: "Do I need to enter a credit card?", a: "Not to see the demo or to scope the deployment. Payment is only collected when you decide to go live on a paid plan." },
                        { id: "branding", q: "Can I customise the branding?", a: "Yes. As the super-admin of your tenant, you can change the logo, the primary brand colour, the company name, the footer address, and the support contact details. The whole platform rebrands instantly." },
                        { id: "data-safety", q: "What about my patient data?", a: "Your data is yours. Isolated per tenant. Daily Mongo backups. You can export anything to CSV from any tab." },
                        { id: "cancel", q: "Can I downgrade or cancel?", a: "Cancel any time from Settings → Subscription before your renewal date. No refund for unused months, but you keep access until the period ends." },
                    ].map((item) => (
                        <details key={item.id} className="group bg-white rounded-xl border border-slate-200 p-5 open:border-cyan-300">
                            <summary className="cursor-pointer list-none flex items-center justify-between text-[15px] font-medium">
                                {item.q}
                                <span className="text-slate-400 group-open:rotate-45 transition-transform">+</span>
                            </summary>
                            <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">{item.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0B1120] text-white py-20">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
                    <h2 className="text-3xl sm:text-4xl font-display tracking-tight">Start running your lab on Toothmatrix today.</h2>
                    <p className="mt-4 text-slate-300/90 leading-relaxed">Book a 30-minute demo and we&rsquo;ll show you the platform on your real workflows. Your own subdomain. Live in under five minutes once you&rsquo;re ready.</p>
                    <button
                        onClick={() => startTrial("annual")}
                        data-testid="cta-request-demo"
                        className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] font-medium text-[14px] hover:bg-cyan-300 transition"
                    >
                        Request a demo <ArrowRight size={15} />
                    </button>
                    <div className="mt-8 text-[12.5px] text-slate-400">
                        Already a customer? <Link to="/login" className="text-cyan-300 hover:underline">Sign in →</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

// ────────────────────────────────────────────────────────────

/**
 * BrandPreview — inline "see your brand live" section above the features grid.
 *
 * The prospect types their lab name → we render a faux browser chrome showing
 * `<their-slug>.toothmatrix.com` with their chosen logo + color + company
 * name. Slug availability is checked live against /api/automaton/slug-available
 * (same endpoint the signup form uses). A "Claim this subdomain" CTA opens
 * the signup modal with these values pre-filled, so the prospect doesn't have
 * to retype them.
 *
 * Conversion lift: B2B SaaS landing pages that show the prospect's *own brand*
 * inline (Notion, Webflow, Slack do this) typically convert ~2x better because
 * the visitor sees themselves on the platform before paying.
 */


function BrandPreview({ preview, setPreview, onClaim }) {
    const [slugStatus, setSlugStatus] = useState(null); // {available, reason}

    // Auto-derive slug from company name when slug is empty
    useEffect(() => {
        if (preview.slug || !preview.company_name) return;
        const auto = preview.company_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 32);
        if (auto.length >= 3) setPreview((p) => ({ ...p, slug: auto }));
    }, [preview.company_name, preview.slug, setPreview]);

    // Debounced slug-availability check (reuses the existing signup endpoint)
    useEffect(() => {
        if (!preview.slug || preview.slug.length < 3) { setSlugStatus(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.get(`${API}/automaton/slug-available`, { params: { slug: preview.slug } });
                setSlugStatus(data);
            } catch (err) {
                console.warn("[brand-preview] slug check failed:", err?.message);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [preview.slug]);

    const onLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { toast.error("Logo preview supports files up to 2 MB"); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            // Persist on shared `preview` state so a future "Continue → contact" CTA
            // can include the lab brand preview in the demo request payload.
            setPreview((p) => ({ ...p, logo_data_url: ev.target?.result?.toString() || null }));
        };
        reader.readAsDataURL(file);
    };
    const logoDataUrl = preview.logo_data_url || null;

    const slugOk = slugStatus?.available === true;
    const slugTaken = slugStatus?.available === false;
    const isEmpty = !preview.company_name?.trim();
    const company = isEmpty ? "Your Lab" : preview.company_name.trim();
    const slug = preview.slug?.trim() || "your-lab";
    const color = preview.color || "#0E7490";
    // Match the testing-agent's expectation: a blank Lab Name shows a placeholder
    // '?' avatar (so Reset feels like a real reset), not the first letter of the
    // fallback string "Your Lab".
    const initial = isEmpty ? "?" : company[0].toUpperCase();

    // Light-vs-dark contrast for the preview chrome's accent button
    const textOnAccent = ((() => {
        // crude luminance check
        try {
            const hex = color.replace("#", "");
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return lum > 0.55 ? "#0f172a" : "#ffffff";
        } catch { return "#ffffff"; }
    })());

    return (
        <section className="bg-white border-y border-slate-200" data-testid="brand-preview-section">
            <div className="max-w-6xl mx-auto px-6 sm:px-8 py-20 grid lg:grid-cols-12 gap-12 items-center">
                {/* Controls */}
                <div className="lg:col-span-5 space-y-5">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700">· See it as yours</div>
                    <h2 className="text-3xl sm:text-4xl font-display tracking-tight leading-[1.1]">
                        Type your lab name.<br />
                        <span className="italic text-slate-600">See your platform live.</span>
                    </h2>
                    <p className="text-[14.5px] text-slate-600 leading-relaxed max-w-md">
                        Your own subdomain. Your logo. Your brand colour. Toothmatrix re-skins itself the moment you sign up — and your patients never see the word &quot;Toothmatrix.&quot;
                    </p>

                    <div className="space-y-3 pt-4">
                        <div>
                            <label className="text-[11px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">Lab name</label>
                            <input
                                data-testid="brand-preview-company"
                                value={preview.company_name}
                                onChange={(e) => setPreview((p) => ({ ...p, company_name: e.target.value }))}
                                maxLength={60}
                                placeholder="Acme Dental Lab"
                                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-cyan-500 outline-none text-[14.5px]"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">Your subdomain</label>
                            <div className="mt-1 flex items-center rounded-lg border border-slate-300 focus-within:border-cyan-500 overflow-hidden">
                                <input
                                    data-testid="brand-preview-slug"
                                    value={preview.slug}
                                    onChange={(e) => setPreview((p) => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                                    maxLength={32}
                                    placeholder="acme"
                                    className="flex-1 px-3 py-2.5 outline-none text-[14.5px]"
                                />
                                <span className="px-3 py-2.5 bg-slate-50 text-slate-500 text-[13.5px] border-l border-slate-200">.toothmatrix.com</span>
                            </div>
                            <div className="mt-1.5 min-h-[18px] text-[11.5px]">
                                {!preview.slug && <span className="text-slate-400">3–32 chars, lowercase a-z 0-9 and dashes</span>}
                                {slugOk && <span className="text-emerald-600 inline-flex items-center gap-1"><Check size={11} /> Available</span>}
                                {slugTaken && <span className="text-rose-600 inline-flex items-center gap-1"><Lock size={11} /> {slugStatus?.reason || "Taken"}</span>}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">Brand colour</label>
                                <input
                                    data-testid="brand-preview-color"
                                    type="color"
                                    value={preview.color}
                                    onChange={(e) => setPreview((p) => ({ ...p, color: e.target.value }))}
                                    className="mt-1 w-full h-10 rounded-lg border border-slate-300 cursor-pointer"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">Logo (preview only)</label>
                                <label className="mt-1 cursor-pointer flex items-center justify-center h-10 rounded-lg border border-dashed border-slate-300 hover:border-cyan-400 text-[12px] text-slate-500">
                                    {logoDataUrl ? <span className="truncate">Logo loaded ✓</span> : <span>Upload</span>}
                                    <input data-testid="brand-preview-logo" type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 pt-4">
                        <button
                            onClick={onClaim}
                            disabled={slugTaken}
                            data-testid="brand-preview-claim"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B1120] text-white font-medium text-[13.5px] hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            <Sparkles size={14} /> Claim {preview.slug ? `${preview.slug}.toothmatrix.com` : "your subdomain"} <ArrowRight size={14} />
                        </button>
                        <button
                            onClick={() => setPreview({ company_name: "", slug: "", color: "#0E7490", logo_data_url: null })}
                            data-testid="brand-preview-reset"
                            className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-[13px]"
                        >
                            <RefreshCw size={13} /> Reset
                        </button>
                    </div>
                    <p className="text-[11.5px] text-slate-400">Book a personalised demo. We&rsquo;ll show you the platform on your real workflows.</p>
                </div>

                {/* Live faux-browser preview */}
                <div className="lg:col-span-7" data-testid="brand-preview-frame">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/40 shadow-xl shadow-slate-900/5 overflow-hidden">
                        {/* Browser chrome */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                            <div className="ml-3 flex-1 flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-slate-200 font-mono-tr text-[11.5px] tracking-tight text-slate-700">
                                <Lock size={11} className="text-emerald-600" />
                                https://<span className="text-slate-900 font-semibold">{slug}</span>.toothmatrix.com<span className="text-slate-400">/admin</span>
                            </div>
                            <Globe size={13} className="text-slate-400" />
                        </div>
                        {/* App preview body */}
                        <div className="bg-white">
                            {/* Skinned header */}
                            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                                <div className="flex items-center gap-2.5">
                                    {logoDataUrl ? (
                                        <img src={logoDataUrl} alt="logo" className="h-9 max-w-[120px] object-contain" />
                                    ) : (
                                        <div
                                            className="h-9 w-9 rounded-lg grid place-items-center font-display text-[15px]"
                                            style={{ background: color, color: textOnAccent }}
                                        >
                                            {initial}
                                        </div>
                                    )}
                                    <div className="font-display text-[15px] tracking-tight text-slate-900">{company}</div>
                                    <div className="ml-1 pl-2.5 border-l border-slate-200 font-mono-tr text-[8.5px] tracking-[0.18em] uppercase text-slate-400 leading-tight">Powered by<br />Toothmatrix</div>
                                </div>
                                <div className="flex items-center gap-3 text-[12px] text-slate-500">
                                    <div className="hidden sm:flex items-center gap-3">
                                        <span>Cases</span>
                                        <span>Invoices</span>
                                        <span>Finance</span>
                                    </div>
                                    <div className="h-7 w-7 rounded-full bg-slate-200" />
                                </div>
                            </div>
                            {/* Skinned content snippet */}
                            <div className="p-5 grid sm:grid-cols-3 gap-3">
                                {[
                                    { k: "Cases today", v: "12", sub: "+3 vs yesterday" },
                                    { k: "Revenue MTD", v: "$50k", sub: "+22%" },
                                    { k: "Late cases", v: "2", sub: "1 in chairside review" },
                                ].map((m) => (
                                    <div key={m.k} className="rounded-xl border border-slate-200 p-3">
                                        <div className="font-mono-tr text-[9.5px] tracking-[0.18em] uppercase text-slate-500">{m.k}</div>
                                        <div className="mt-1 text-2xl font-display tracking-tight" style={{ color }}>{m.v}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{m.sub}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="px-5 pb-5">
                                <div className="rounded-xl border border-slate-200 overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                                        <div className="font-display text-[13.5px] text-slate-900">Recent cases</div>
                                        <button className="text-[11px] rounded-md px-2 py-1" style={{ background: color, color: textOnAccent }}>+ New case</button>
                                    </div>
                                    <ul className="divide-y divide-slate-100 text-[12.5px]">
                                        {[
                                            { num: "TR-0142", clinic: "Bay View Dental", state: "Manufacturing", at: "3h" },
                                            { num: "TR-0141", clinic: "City Smile Studio", state: "QA", at: "5h" },
                                            { num: "TR-0140", clinic: "Coastal Implants", state: "Delivered", at: "1d" },
                                        ].map((c) => (
                                            <li key={c.num} className="px-4 py-2 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono-tr text-[11.5px] text-slate-600">{c.num}</span>
                                                    <span className="text-slate-900">{c.clinic}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="px-2 py-0.5 rounded-full text-[10.5px]" style={{ background: `${color}15`, color }}>{c.state}</span>
                                                    <span className="text-slate-400">{c.at}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────

